import React, { useMemo } from "react";
import { Direction, IBooking, IBookingJourney, IBookingJourneySegment, IBookingTariffSegment, useStateSelector } from "../Models";
import { getStrings } from "../../Shared/Reservation/StringResources";
import dayjs from "dayjs";
import { formatDuration } from "../../Shared/Utils";
import { getSettings, travelMethodClass } from "../Settings";
import _ from "underscore";
import SvgAlertTriangle from "../../Shared/Icons/bootstrap/AlertTriangle";
import { Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { openSeatmap } from "../Actions";

const strings = getStrings().component.orderSummary;
// const commonStrings = getStrings().component.common;

export const BookingSummary = (props: { booking: IBooking }) => {
	if (!props.booking)
		return null;


	return <div className="order-summary">
		<p className="intro-text">{strings.intro}</p>
		<JourneySummary journey={props.booking.outbound} direction="outbound" />
		{props.booking.inbound && <JourneySummary journey={props.booking.inbound} direction="inbound" />}
	</div>;
};

const JourneySummary = (props: { journey: IBookingJourney, direction: Direction }) => {
	if (!props.journey)
		return null;

	const journey = props.journey;
	const travelWithPet = useStateSelector(state => state.search.travelWithPet);
	const settings = getSettings();

	const departure = dayjs(journey.departure);
	const arrival = dayjs(journey.arrival);
	const duration = dayjs.duration(arrival.diff(departure));

	const petInventoryClass = useMemo(() => findPetInventoryClass(props.journey), [props.journey]);
	const showPetCompartmentWarning = !travelWithPet && !!petInventoryClass;

	return <div className="journey-summary">
		<h1>{journey.departureStation.name} - {journey.arrivalStation.name}</h1>
		{ showPetCompartmentWarning && <p><SvgAlertTriangle /> { petInventoryClass === "PC" ? strings.petWarningPC : strings.petWarningPS }</p>}
		<dl>
			<dt>{strings.departure}</dt>
			<dd>{dayjs(journey.departure).format("LLLL")}</dd>
			<dt>{strings.arrival}</dt>
			<dd>{dayjs(journey.arrival).format("LLLL")}</dd>
			<dt>{strings.duration}</dt>
			<dd>{formatDuration(duration)},&nbsp;
				{journey.totalSegmentCount &&
				(journey.totalSegmentCount === 1 ?
					strings.direct :
					(journey.totalSegmentCount === 2 ? `1 ${strings.change}` : `${journey.totalSegmentCount-1} ${strings.changes}`))}
			</dd>
			{/* { journey.optionSummary && journey.optionSummary.length > 0 &&
			<>
				<dt>{strings.options}</dt>
				<dd>
					<ul className="list-unstyled">
					{journey.optionSummary.map((x, idx) => <li key={idx}>{x.description} × {x.count}</li>)}
					</ul>
				</dd>
			</>
			} */}
		</dl>

		{journey.bookingTariffSegments.map(bts =>
			bts.bookingJourneySegments.map((s, idx) => <SegmentSummary key={idx} segment={s} tariffSegment={bts} seatmapEnabled={settings.enableSeatmap} />)
		)}
	</div>;
};

const SegmentSummary = (props: { segment: IBookingJourneySegment, tariffSegment: IBookingTariffSegment, seatmapEnabled?: boolean }) => {

	const dispatch = useDispatch();

	const optionsByCode = _.groupBy(props.tariffSegment.additionalProducts || [], x => x.code);
	const options = Object.getOwnPropertyNames(optionsByCode).map(code => ({ code, name: optionsByCode[code][0].name, count: optionsByCode[code].length}));

	const showSeatmapLink = props.seatmapEnabled && props.tariffSegment.requiredProducts.some(x => x.seat) && props.tariffSegment.requiredProducts.every(x => x.canChangeSeat); //TODO: Inte egen kupé! Kanske inte heller sittplats i kupé?

	const changeSeatsClicked = () => {
		dispatch(openSeatmap(props.tariffSegment));
	}


	return <div className="segment-summary">
		<h3><span className={`travelmethod-icon ${travelMethodClass(props.segment.serviceType.modality)}`} title={props.segment.serviceType.name}></span>{props.segment.departureStation.name} - {props.segment.arrivalStation.name}</h3>
			<p className="segment-info">{props.segment.serviceType.name}{/*, {props.transportInfo.comfortPresentationText} */}</p>
		{/* {description ?
			<p>{description}</p> :
			props.transportInfo.passengerInfo.map((p, idx) => <p key={idx}>{getPlacementDescription(p)}</p>)} */}
		{props.tariffSegment.requiredProducts.filter(x => x.seat).map(p => <p key={p.itemId}>{getPlacementDescription(p.seat)}</p>)}
		{props.tariffSegment.requiredProducts.every(x => !x.seat) && strings.noSeatReservation }
		{ showSeatmapLink && <Button variant="link" onClick={changeSeatsClicked}>{strings.changeSeats}</Button> }
		{options.length > 0 && <>
			<h4>Tillval</h4>
			<ul className="list-unstyled">
				{options.map(x => <li key={x.code}>{x.name} × {x.count}</li>)}
			</ul>
		</>}
	</div>
}

function getPlacementDescription(passengerInfo: { carriage?: string; number?: string; compartment?: string; }) {
	let desc = `${strings.carriage} ${passengerInfo.carriage}`;
	if (passengerInfo.number)
		desc += `, ${strings.seat} ${passengerInfo.number}`;
	if (passengerInfo.compartment)
		desc += `, ${strings.compartment} ${passengerInfo.compartment}`;

	// let seatInfo = [passengerInfo.orientation, passengerInfo.characteristic].filter(x => x !== undefined) as string[];
	// if (seatInfo.length > 0)
	// 	desc += ` (${seatInfo.join(", ")})`;

	return upperCaseFirst(desc);
}

function findPetInventoryClass(journey: IBookingJourney) {
	if (!journey || !journey.bookingTariffSegments)
		return null;

	for(let i=0; i<journey.bookingTariffSegments.length; i++) {
		const segment = journey.bookingTariffSegments[i];
		for (let j=0; j<segment.requiredProducts.length; j++) {
			const product = segment.requiredProducts[j];
			if (product.inventoryClass === "PS" || product.inventoryClass == "PC")
				return product.inventoryClass;
		}
	}
	return null;
}

const upperCaseFirst = (str: string) =>  str.substring(0, 1).toUpperCase() + (str.substring(1).toLowerCase());
